<script setup lang="ts">
import MarketingFlowTriggerTypeBaseWrap from './BaseWrap.vue'

import type { TriggerSettings } from '../../types/marketingFlow'
import type { Audience } from '@/types/types'

const { t } = useI18n()
const props = defineProps<{
  audiences: Audience[]
  triggerSegmentId?: number
}>()

const triggerSettings = defineModel<TriggerSettings>('triggerSettings')

const availableIncludedAudiences = computed(() => {
  const audienceTriggerSegment = props.triggerSegmentId
    ? [props.audiences.find((a) => a.id === props.triggerSegmentId)]
    : []

  return props.audiences.filter(
    (a) =>
      !triggerSettings.value.excludedSegments?.some((b) => b.id === a.id) &&
      !audienceTriggerSegment.some((b) => b.id === a.id)
  )
})

const availableExcludedAudiences = computed(() => {
  const audienceTriggerSegment = props.triggerSegmentId
    ? [props.audiences.find((a) => a.id === props.triggerSegmentId)]
    : []

  return props.audiences.filter(
    (a) =>
      !triggerSettings.value.includedSegments?.some((b) => b.id === a.id) &&
      !audienceTriggerSegment.some((b) => b.id === a.id)
  )
})

const includedSegmentIds = computed(() => triggerSettings.value.includedSegments?.map((a) => a.id) ?? [])
const excludedSegmentIds = computed(() => triggerSettings.value.excludedSegments?.map((a) => a.id) ?? [])

const idsToAudiences = (ids: number[], existingAudiences: Audience[] | null): Audience[] => {
  return ids
    .map((id) => props.audiences.find((a) => a.id === id) ?? existingAudiences?.find((a) => a.id === id))
    .filter((a): a is Audience => Boolean(a))
}

const onIncludedChange = (ids: number[]) => {
  const audiences = idsToAudiences(ids, triggerSettings.value.includedSegments)
  triggerSettings.value.includedSegments = audiences.length ? audiences : null
  emit('onChanges', { isCritical: true, field: 'includedSegments', value: audiences })
}

const onExcludedChange = (ids: number[]) => {
  const audiences = idsToAudiences(ids, triggerSettings.value.excludedSegments)
  triggerSettings.value.excludedSegments = audiences.length ? audiences : null
  emit('onChanges', { isCritical: true, field: 'excludedSegments', value: audiences })
}

const emit = defineEmits<{
  (e: 'onChanges', { isCritical, field, value }): void
}>()
</script>

<template>
  <MarketingFlowTriggerTypeBaseWrap
    icon="HeroSquare3Stack3DIconOutline"
    :title="t('pages.marketingFlow.marketingFlowEditPage.trigger.segments')"
  >
    <div class="flex flex-col gap-1.5">
      <label class="text-sm text-foreground">
        {{ t('pages.marketingFlow.marketingFlowEditPage.trigger.includedSegments') }}
      </label>

      <MultiSelectAudiences
        :model-value="includedSegmentIds"
        :audiences="availableIncludedAudiences"
        :default-text="t('pages.marketingFlow.marketingFlowEditPage.trigger.allCustomers')"
        @update:model-value="onIncludedChange"
      />
    </div>

    <div class="flex flex-col gap-1.5 mt-3">
      <label class="text-sm text-foreground">
        {{ t('pages.marketingFlow.marketingFlowEditPage.trigger.excludedSegments') }}
      </label>
      <MultiSelectAudiences
        :model-value="excludedSegmentIds"
        :audiences="availableExcludedAudiences"
        :default-text="t('pages.marketingFlow.marketingFlowEditPage.trigger.excludeSegmentPlaceholder')"
        @update:model-value="onExcludedChange"
      />
    </div>
  </MarketingFlowTriggerTypeBaseWrap>
</template>
